import { Button, Icon, ISelectOption, Label, Pullout, PulloutType } from '@aon/aon-tws-common-ui';
import globalVars from '@aon/aon-tws-common-ui/build/lib/globalVars.module.scss';
import * as React from 'react';
import HtmlParser from 'html-react-parser';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../appStore';
import ContentConstants from '../../contentConstants';
import {
	ActionType,
	AudienceSourceType,
	ConditionSourceType,
	DisplayLogicPropertyUnderValidation,
	IDisplayLogicAttribute,
	IDisplayLogicCondition,
	IDisplayLogicEditModel,
	IDisplayLogicQuestion,
	IValidationCollection,
	SurveyPropertyUnderValidation,
} from '../../models';
import {
	addNewCondition,
	attributeChange,
	conditionQuestionChange,
	conditionResponseChange,
	deleteCondition,
	selectDisplayLogicQuestions,
	selectionDismissal,
	updateActionType,
	updateDestinationQuestionId,
} from '../../store/QuestionBranchingLogicSlice';
import { selectSurveyQuestion } from '../../store/SurveyStore';
import { ValidationCollectionHelper } from '../../utilities';
import { assertNonNullable } from '../../utilities/assertions';
import { useBranchingLogicAttributes, useBranchingLogicHierarchies } from '../../utilities/hooks';
import { ClearDiv, ConditionContainer } from '../common';
import { AddConditionsDropDown, DisabledReason, IDisabledReason } from '../displayLogic/AddConditionsDropDown';
import { AttributeBranchingLogic } from './AttributeBranchingLogic';
import { BranchingLogicRuleAction } from './BranchingLogicRuleAction';
import { HierarchyBranchingLogic } from './HierarchyBranchingLogic';
import { PreviousQuestionBranchingLogic } from './PreviousQuestionBranchingLogic';
import { QuestionBranchingLogicScope } from './QuestionBranchingLogicScope';

export interface IQuestionBranchingLogicProps {
	scope: QuestionBranchingLogicScope;
	branchingLogic: IDisplayLogicEditModel;
	branchingLogicIndex: number;
	validationResults?: IValidationCollection;
	dataMissingValidationResults?: IValidationCollection;
}

const ConditionsContainer = styled.div`
	width: 100%;
	text-align: left;
`;
ConditionsContainer.displayName = 'ConditionsContainer';

const DataMissingContainer = styled.div`
	width: 50%;
	margin-left: auto;
	margin-right: auto;
`;
DataMissingContainer.displayName = 'DataMissingContainer';

export const BranchingLogicRuleContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem;
	background-color: ${globalVars.secondaryBlue};
	border-radius: 4px;
`;
ConditionContainer.displayName = 'BranchingLogicRuleContainer';

export const BranchingLogicRule = ({
	scope,
	branchingLogic,
	branchingLogicIndex,
	validationResults,
	dataMissingValidationResults,
}: IQuestionBranchingLogicProps) => {
	const dispatch = useAppDispatch();

	const { actionType, destinationQuestionId } = branchingLogic.displayLogicRule.displayLogicAction;

	const {
		audienceSource: { audienceSourceType },
	} = useAppSelector((state) => state.survey.surveyEditModel);
	const { attributes } = useBranchingLogicAttributes();
	const { selectableHierarchies } = useBranchingLogicHierarchies();

	const question = useAppSelector((state) => selectSurveyQuestion(state, branchingLogic.displayLogicRule.surveyQuestionId));
	assertNonNullable(question);
	assertNonNullable(question.displayOrder);

	const questionDisplayOrder = question?.displayOrder ?? 1;
	const maxDisplayOrder = actionType === ActionType.BranchToQuestion ? questionDisplayOrder + 1 : questionDisplayOrder;
	const displayLogicQuestions = useAppSelector((state) =>
		selectDisplayLogicQuestions(state, {
			maxDisplayOrder,
			hasResponseOptions: true,
		}),
	);
	const allUnusedDisplayLogicQuestions = getUnusedQuestions();

	const allUnusedAttributes = getUnusedAttributes();
	const attributeDataCouldNotBeFetched: DisabledReason = {
		disabled: (attributes?.isLoading === false && attributes.couldNotFetchData) || allUnusedAttributes.length === 0,
		reason: audienceSourceType === AudienceSourceType.FileUpload ? IDisabledReason.NoDistribution : IDisabledReason.WrongAudienceSourceType,
	};
	const hierarchyDataCouldNotBeFetched: DisabledReason = {
		disabled:
			audienceSourceType === AudienceSourceType.FileUpload ||
			[...branchingLogic.displayLogicConditions.map((c) => c.hierarchy), selectableHierarchies].some(
				(dep) => dep?.isLoading === false && dep.couldNotFetchData,
			),
		reason: audienceSourceType === AudienceSourceType.FileUpload ? IDisabledReason.WrongAudienceSourceType : IDisabledReason.NoDistribution,
	};

	const validationCollectionHelper = new ValidationCollectionHelper<DisplayLogicPropertyUnderValidation>(validationResults);
	const actionValidationResults = validationCollectionHelper.GetValidationResultForProperty(
		DisplayLogicPropertyUnderValidation.DisplayLogicAction,
	);
	const conditionValidationResults = validationCollectionHelper.GetValidationResultForProperty(
		DisplayLogicPropertyUnderValidation.DisplayLogicCondition,
	)?.propertyErrors;

	const valueValidationResults = validationCollectionHelper.GetValidationResultForProperty(
		DisplayLogicPropertyUnderValidation.DisplayLogicValue,
	)?.propertyErrors;

	return (
		<div className='d-flex flex-column'>
			{renderPulloutHeaderSection()}
			<BranchingLogicRuleContainer className='tws-branching-rule' role='group' aria-label={`Branching Logic Rule ${branchingLogicIndex}`}>
				<Label>Selected Rule:</Label>
				<div className='row'>
					<div className='col'>
						<BranchingLogicRuleAction
							actionType={actionType}
							destinationQuestionId={destinationQuestionId}
							questionDisplayOrder={question.displayOrder}
							validationResult={actionValidationResults}
							scope={scope}
							onActionTypeChange={handleActionTypeChange}
							onDestinationQuestionChange={handleDestinationQuestionChange}
						/>
					</div>
				</div>
				{branchingLogic.displayLogicConditions.map((condition) => {
					const conditionMessages =
						conditionValidationResults && Object.prototype.hasOwnProperty.call(conditionValidationResults, condition.index)
							? conditionValidationResults[condition.index]
							: [];
					const valueMessages =
						valueValidationResults && Object.prototype.hasOwnProperty.call(valueValidationResults, condition.index)
							? valueValidationResults[condition.index]
							: [];
					return (
						<div
							key={condition.index}
							className='row tws-branching-condition'
							role='group'
							aria-label={`Branching Logic Condition ${condition.index}`}
						>
							<div className='col-11'>
								{condition.conditionSourceType === ConditionSourceType.PreviousQuestion && (
									<PreviousQuestionBranchingLogic
										unusedDisplayLogicQuestions={getUnusedQuestionsForCondition(condition)}
										surveyQuestionId={branchingLogic.displayLogicRule.surveyQuestionId}
										condition={condition}
										HandleQuestionChange={handleQuestionChange}
										conditionMessages={conditionMessages}
										valueMessages={valueMessages}
										HandleResponseChange={handleResponseChange}
										HandleSelectionDismissal={handleSelectionDismissal}
									/>
								)}
								{(condition.conditionSourceType === ConditionSourceType.FileAttribute ||
									condition.conditionSourceType === ConditionSourceType.HrisAttribute) && (
									<AttributeBranchingLogic
										unusedAttributes={getUnusedAttributesForCondition(condition)}
										conditionMessages={conditionMessages}
										valueMessages={valueMessages}
										condition={condition}
										HandleAttributeChange={handleAttributeChange}
										HandleResponseChange={handleResponseChange}
										HandleSelectionDismissal={handleSelectionDismissal}
									/>
								)}
								{condition.conditionSourceType === ConditionSourceType.HierarchyNode && (
									<HierarchyBranchingLogic
										conditionMessages={conditionMessages}
										valueMessages={valueMessages}
										condition={condition}
										branchingLogicIndex={branchingLogicIndex}
										audienceSourceType={audienceSourceType || AudienceSourceType.Hris}
									/>
								)}
							</div>
							<div className='col-1'>
								<Button
									color='transparent'
									aria-label={`Delete condition ${condition.index}`}
									onClick={() => deleteBranchingLogic(condition.index)}
								>
									<Icon name='delete' />
								</Button>
							</div>
						</div>
					);
				})}
				<div className='row'>
					<div className='col-auto'>
						<AddConditionsDropDown
							actionType={actionType}
							audienceSourceType={audienceSourceType}
							noAvailableDisplayLogicQuestions={allUnusedDisplayLogicQuestions.length < 1}
							attributeDisabled={attributeDataCouldNotBeFetched}
							hierarchyDisabled={hierarchyDataCouldNotBeFetched}
							onConditionMenuItemClick={handleAddConditionChange}
						/>
					</div>
				</div>
			</BranchingLogicRuleContainer>
		</div>
	);

	function renderPulloutHeaderSection() {
		const errors = scope === 'individual' && Errors();
		const warnings = Warnings();
		const dispPulloutsFor = [
			...Object.entries(errors).map(([key, error]) => ({ type: PulloutType.Error, key, message: error })),
			...Object.entries(warnings).map(([key, warning]) => ({ type: PulloutType.Warning, key, message: warning })),
		];

		if (dispPulloutsFor.length === 0) {
			return null;
		}

		return (
			<DataMissingContainer>
				{dispPulloutsFor.map((pullout) => (
					<div key={pullout.key}>
						<Pullout type={pullout.type}>{typeof pullout.message === 'string' ? HtmlParser(pullout.message) : pullout.message}</Pullout>
						<br />
					</div>
				))}
				<ClearDiv />
				<ClearDiv />
				<ClearDiv />
			</DataMissingContainer>
		);
	}

	function Warnings() {
		return {
			...(() => {
				const dependencies = [attributes, selectableHierarchies, ...branchingLogic.displayLogicConditions.map((c) => c.hierarchy)];

				if (dependencies.some((dep) => dep && !dep.isLoading && dep.couldNotFetchData)) {
					return { 'Dependencies N/A': ContentConstants.EMPLOYEEDATASOURCE_NOTAVAIL_WARNING };
				}
			})(),
		};
	}

	function Errors() {
		return {
			...(() => {
				const validationResult = new ValidationCollectionHelper(dataMissingValidationResults).GetValidationResultForProperty(
					SurveyPropertyUnderValidation.displayLogic,
				);

				const validationErrors = validationResult?.propertyErrors?.[branchingLogic.displayLogicRule.surveyQuestionId];

				if (validationErrors && validationErrors.length > 0) {
					return {
						'Data Missing': (
							<>
								<strong>{ContentConstants.DISPLAYLOGIC_DATA_MISSING_ERROR}</strong> <br />
								<br />
								{validationErrors.map((e) => {
									return (
										<span key={e}>
											<span style={{ fontWeight: 500 }}> - {e}</span>
											<br />
											<br />
										</span>
									);
								})}
								<strong>{ContentConstants.DISPLAYLOGIC_DATA_MISSING_SAVE_MESSAGE}</strong>
							</>
						),
					};
				}
			})(),
		};
	}

	function handleActionTypeChange(value: ActionType) {
		dispatch(
			updateActionType({
				actionType: value,
				branchingLogicIndex,
			}),
		);
	}

	function handleDestinationQuestionChange(value?: number) {
		dispatch(
			updateDestinationQuestionId({
				destinationQuestionId: value,
				branchingLogicIndex,
			}),
		);
	}

	function getAttributesInUse() {
		return new Set(
			branchingLogic.displayLogicConditions
				.filter(isAttributeCondition)
				.map((condition) => condition.conditionSourceId)
				.filter((id) => id),
		);
	}

	function getUnusedAttributes() {
		if (!attributes || attributes.isLoading || attributes.couldNotFetchData) {
			return [];
		}

		const attributesInUse = getAttributesInUse();

		return attributes.filter((attr) => !attributesInUse.has(attr.columnIndex.toString()));
	}

	function getUnusedAttributesForCondition(condition: IDisplayLogicCondition) {
		if (!attributes || attributes.isLoading || attributes.couldNotFetchData) {
			return [];
		}

		const BranchingLogicAttributes: IDisplayLogicAttribute[] = attributes as IDisplayLogicAttribute[];
		return BranchingLogicAttributes.filter(
			(attr) =>
				!branchingLogic.displayLogicConditions.some((c) => c.conditionSourceText === attr.name) ||
				condition.conditionSourceText === attr.name,
		);
	}

	function getUnusedQuestionsForCondition(condition: IDisplayLogicCondition) {
		const BranchingLogicQuestions: IDisplayLogicQuestion[] = displayLogicQuestions;
		return BranchingLogicQuestions.filter(
			(q) =>
				!branchingLogic.displayLogicConditions.some((c) => c.conditionSourceId === q.surveyQuestionId.toString()) ||
				condition.conditionSourceId === q.surveyQuestionId.toString(),
		);
	}

	function getUnusedQuestions() {
		const questionsInUse = new Set(
			branchingLogic.displayLogicConditions
				.filter((c) => c.conditionSourceType === ConditionSourceType.PreviousQuestion)
				.map((c) => c.conditionSourceId)
				.filter((id) => id),
		);

		return displayLogicQuestions.filter((q) => !questionsInUse.has(q.surveyQuestionId.toString()));
	}

	function isAttributeCondition(condition: IDisplayLogicCondition) {
		return (
			condition.conditionSourceType === ConditionSourceType.HrisAttribute ||
			condition.conditionSourceType === ConditionSourceType.FileAttribute
		);
	}

	function handleAddConditionChange(conditionSourceType: ConditionSourceType) {
		dispatch(
			addNewCondition({
				conditionSourceType: conditionSourceType,
				branchingLogicIndex: branchingLogicIndex,
			}),
		);
	}

	function handleQuestionChange(surveyQuestionId: number, questionText: string, index: number): void {
		dispatch(
			conditionQuestionChange({
				conditionIndex: index,
				branchingLogicIndex: branchingLogicIndex,
				questionId: surveyQuestionId,
				questionText,
			}),
		);
	}

	function handleResponseChange(responses: string[], index: number): void {
		dispatch(conditionResponseChange({ conditionIndex: index, branchingLogicIndex: branchingLogicIndex, responses }));
	}

	function deleteBranchingLogic(conditionIndex: number): void {
		dispatch(deleteCondition({ conditionIndex, branchingLogicIndex: branchingLogicIndex }));
	}

	function handleSelectionDismissal(conditionIndex: number, values: ISelectOption[]): void {
		dispatch(selectionDismissal({ conditionIndex, branchingLogicIndex: branchingLogicIndex, values }));
	}

	function handleAttributeChange(columnIndex: number, attribute: IDisplayLogicAttribute, conditionIndex: number): void {
		dispatch(
			attributeChange({
				conditionIndex,
				branchingLogicIndex,
				attributeName: attribute.name,
				selectedAttributeId: columnIndex.toString(),
			}),
		);
	}
};
